import React from 'react';
import moment from 'moment';
import ReactJson from 'react-json-view';
import {Button, Card, Input, Pagination, Row, Table, Tabs, Icon, Spin, Collapse, Dropdown, Select, DatePicker, Checkbox, Timeline, Alert} from 'antd';
import {Payment, PaymentHistoryProps, PaymentHistoryState, FilterType, PaymnetTypes, ErrorTypes, PayStatusTypes} from '../interfaces';
import ExportModal from './ExportModal';
import './style.css';

const {TabPane} = Tabs;
const {Panel} = Collapse;

class PaymentHistory extends React.PureComponent<PaymentHistoryProps, PaymentHistoryState> {
  state: PaymentHistoryState = {
    activeKey: '',
    filter: {
      fulltext: '',
      dateFrom: '',
      dateTo: '',
      paymentTypes: [],
      errorTypes: [],
      payStatusTypes: [],
      clubIds: [],
    },
    isDropdownOpen: false,
    currentPage: 1,
    isExportModalOpen: false,
  };

  filterDebounce: NodeJS.Timeout = null;

  componentDidMount() {
    window.addEventListener('click', this.clickedOnScreen);
  }

  componentWillUnmount() {
    window.removeEventListener('click', this.clickedOnScreen);
  }

  /** Refetches payments with current parameters */
  refetchData = () => {
    const {refetch, regionId} = this.props;
    const {filter, currentPage} = this.state;
    refetch({regionId, first: 10, after: (currentPage - 1) * 10, filter: JSON.stringify(filter)});
  };

  /** Refetch data after page changes */
  onPaginationChange = (pageNumber: number) => {
    this.setState({currentPage: pageNumber}, this.refetchData);
  };

  /** Add hashNumber to location and expands payment details */
  onPanelChange = (expanded: boolean, {reference}: {reference: string}): void => {
    this.setState({activeKey: expanded ? reference : null});
  };

  /** Handling export CSV Modal */
  openExportModal = () => {
    this.setState({isExportModalOpen: true});
  };
  closeExportModal = () => {
    this.setState({isExportModalOpen: false});
  };

  /***************************************************************************** Filter **********************************************************************************/

  /** Open/Close filter drop down */
  toggleDropDown = () => this.setState({isDropdownOpen: !this.state.isDropdownOpen});

  /** Closes dropdown when you click outside dropdown */
  clickedOnScreen = (e: any) => {
    const path = e.path || (e.composedPath && e.composedPath());
    if (path && Array.isArray(path)) {
      const classPath = path.map(item => item.className).filter(item => !!item);
      if (this.state.isDropdownOpen && classPath.length) {
        if (!classPath.some(item => item.includes('dropdown-tools-panel') || item.includes('filter-dropdown') || item.includes('ant-calendar'))) {
          this.setState({isDropdownOpen: false});
        }
      }
    }
  };

  /** Apply new filter */
  setFilter = (key: FilterType, value: any) => this.setState({filter: {...this.state.filter, [key]: value}});

  /** Wrapper for {@link setFilter} function */
  setFullTextFilter = (e: React.ChangeEvent<HTMLInputElement>) => this.setFilter(FilterType.FULLTEXT, e.target.value);
  setDateFromFilter = (value: moment.Moment) => this.setFilter(FilterType.DATE_FROM, value && value.toString());
  setDateToFilter = (value: moment.Moment) => this.setFilter(FilterType.DATE_TO, value && value.toString());
  setPaymentTypesFilter = (value: string[]) => this.setFilter(FilterType.PAYMENT_TYPES, value);
  setErrorTypesFilter = (value: string[]) => this.setFilter(FilterType.ERROR_TYPES, value);
  setPaymentStatusTypesFilter = (value: string[]) => this.setFilter(FilterType.PAY_STATUS_TYPES, value);
  setClubsFilter = (value: string[]) => this.setFilter(FilterType.CLUBIDS, value);

  getFilter() {
    const {filter} = this.state;
    return (
      <div className="dropdown-tools-panel">
        <Collapse>
          <Panel key={'1'} header="Submission Date">
            <div style={{marginBottom: 5}}>
              <span style={{display: 'block'}}>From</span>
              <DatePicker
                placeholder="From"
                onChange={this.setDateFromFilter}
                value={filter.dateFrom && moment(filter.dateFrom)}
                format="DD/MM/YYYY HH:mm"
                style={{width: '100%'}}
                showTime={{format: 'HH:mm'}}
              />
            </div>
            <div>
              <span style={{display: 'block'}}>To</span>
              <DatePicker
                placeholder="To"
                onChange={this.setDateToFilter}
                value={filter.dateTo && moment(filter.dateTo)}
                format="DD/MM/YYYY HH:mm"
                style={{width: '100%'}}
                showTime={{format: 'HH:mm'}}
              />
            </div>
          </Panel>
          <Panel key={'2'} header="Payment type">
            <Checkbox.Group
              value={filter.paymentTypes}
              onChange={this.setPaymentTypesFilter}
              options={[
                {label: PaymnetTypes.OSP, value: PaymnetTypes.OSP},
                {label: PaymnetTypes.DSP, value: PaymnetTypes.DSP},
                {label: PaymnetTypes.DEBT, value: PaymnetTypes.DEBT},
              ]}
            />
          </Panel>
          <Panel key={'3'} header="Error type">
            <Checkbox.Group
              value={filter.errorTypes}
              onChange={this.setErrorTypesFilter}
              options={[
                {label: ErrorTypes.WITH, value: ErrorTypes.WITH},
                {label: ErrorTypes.WITHOUT, value: ErrorTypes.WITHOUT},
              ]}
            />
          </Panel>
          <Panel key={'4'} header="Pay status type">
            <Checkbox.Group
              value={filter.payStatusTypes}
              onChange={this.setPaymentStatusTypesFilter}
              options={[
                {label: PayStatusTypes.PAID, value: PayStatusTypes.PAID},
                {label: PayStatusTypes.UNPAID, value: PayStatusTypes.UNPAID},
              ]}
            />
          </Panel>
        </Collapse>
      </div>
    );
  }

  renderToolbar() {
    const {loading, clubs} = this.props;
    const {isDropdownOpen, filter} = this.state;

    const antIcon = <Icon type="loading" spin />;

    return (
      <Card className={'filter-card'}>
        <div className={'export-button-wrapper'}>
          <Button size={'small'} type="primary" onClick={this.openExportModal}>
            Export CSV
          </Button>
        </div>
        <div className={'filter-wrapper'}>
          <div>
            <Input value={filter.fulltext} className={'filter-input'} onChange={this.setFullTextFilter} placeholder="Filter by reference or member ID" />
            <Dropdown visible={isDropdownOpen} overlay={this.getFilter()} placement="bottomRight">
              <Button onClick={this.toggleDropDown} className={'filter-dropdown'}>
                Filter
                <Icon type={isDropdownOpen ? 'close' : 'bars'} />
              </Button>
            </Dropdown>
            {loading ? (
              <Spin className={'spinner'} indicator={antIcon} />
            ) : (
              <Button onClick={this.refetchData} className={'filter-button'} type="primary">
                Filter
              </Button>
            )}
            <div>
              <Select className={'filter-select'} mode="multiple" placeholder="Clubs" value={filter.clubIds} onChange={this.setClubsFilter}>
                {clubs
                  .sort((a, b) => (a.name < b.name ? -1 : 1))
                  .map((club, i) => (
                    <Select.Option value={club.id} key={i.toString()}>
                      {club.name}
                    </Select.Option>
                  ))}
              </Select>
              <div style={{paddingTop: 5}}>
                <Button onClick={() => this.setClubsFilter(clubs.map(club => club.name))}>Add All Clubs</Button>
                <Button style={{marginLeft: 5}} onClick={() => this.setClubsFilter([])}>
                  Remove All Clubs
                </Button>
              </div>
            </div>
          </div>
        </div>
      </Card>
    );
  }

  /***************************************************************************** Filter **********************************************************************************/

  renderExpandedRow = (payment: Payment) => (
    <Row gutter={24}>
      <Tabs defaultActiveKey="1">
        <TabPane tab="Payment history" key="1">
          <Timeline>
            {payment.history.map((item: any, i: number) => {
              const getColor = (type: any, err: any) => {
                switch (type) {
                  case 'info':
                    return 'green';
                  case 'error':
                    return 'red';
                  default:
                    return 'orange';
                }
              };
              return (
                <Timeline.Item color={getColor(item.type, item.err)} key={i}>
                  <span>{item.message}</span>
                  {item.err && (
                    <span>
                      <Alert message={<ReactJson name={false} src={item.err} collapsed />} type={item.type || 'error'} showIcon />
                    </span>
                  )}
                </Timeline.Item>
              );
            })}
          </Timeline>
        </TabPane>
      </Tabs>
    </Row>
  );

  render() {
    const {filter, isExportModalOpen, activeKey} = this.state;
    const {regionId, columns, totalPages, paymentData} = this.props;

    return (
      <div>
        <ExportModal
          isOpen={isExportModalOpen}
          onCancel={this.closeExportModal}
          regionId={regionId}
          filter={JSON.stringify(filter)}
        />
        {this.renderToolbar()}
        <Card bodyStyle={{padding: 0}}>
          <Table
            columns={columns}
            pagination={false}
            expandedRowKeys={activeKey ? [activeKey] : []}
            onExpand={this.onPanelChange}
            expandedRowRender={this.renderExpandedRow}
            dataSource={paymentData.map((o: Payment) => ({...o, key: o.reference}))}
            scroll={{x: 'fit-content'}}
          />
          <Pagination className={'ext-pagination'} total={totalPages} onChange={this.onPaginationChange} />
        </Card>
      </div>
    );
  }
}

export default PaymentHistory;



// WEBPACK FOOTER //
// ./src/pages/PaymentHistoryPage/components/PaymentHistory.tsx